/**
 * The profile's building blocks, ported from A2 `profile/controls.tsx`
 * (`Card`, `Row`, `Why`) and `profile/sections.tsx` (`Section` :178-216,
 * `Missing` :218, `Flash` :755-762), plus the skeleton and the load-failure
 * card A2 never needed because its data was a module constant.
 */
import {
  "profile.load_failed" as loadFailedText,
  "profile.missing_badge" as missingBadge,
  "profile.retry" as retryText,
  "profile.why_prefix" as whyPrefix,
} from "@repo/i18n";
import { ChevronRight, Info } from "lucide-react";
import { createContext, type ReactNode, useCallback, useContext } from "react";
import { cx } from "../../../lib/cx";
import { Chip, Icon } from "../../../ui/controls";
import type { EditorId } from "../editors";
import type { Completeness } from "./completeness";
import { SECTION_META, type SectionId } from "./section-meta";
import type { ProfileData } from "./use-profile-data";

/** What a data section receives: the reads, the score, and "now". */
export interface DataSectionProps extends SectionProps {
  readonly complete: Completeness | null;
  readonly data: ProfileData;
  readonly now: Date;
}

/** What every section receives from the overlay. */
export interface SectionProps {
  /** The row id just saved, for the `p-saved` flash, or `null`. */
  readonly flash: string | null;
  /** Opens an editor: a PUSH of `edit=<id>` (T10's `openLayer`). */
  readonly open: (id: EditorId) => void;
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx("overflow-hidden rounded-2xl bg-surface shadow", className)}
    >
      {children}
    </div>
  );
}

export interface RowProps {
  readonly first?: boolean;
  readonly hint?: ReactNode;
  readonly label: string;
  /** An answer the plan wants and does not have: ember value and a dot. */
  readonly missing?: boolean;
  /** Absent: the row is read-only (a `div`, no chevron). */
  readonly onClick?: () => void;
  readonly value: ReactNode;
}

/** An answer: what it is, small; the answer itself, prominent. Tapping edits it. */
export function Row({ label, value, onClick, first, missing, hint }: RowProps) {
  const isMissing = missing ?? false;
  const body = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block text-ink-3 text-sm leading-tight">{label}</span>
        <span
          className={cx(
            "mt-1 block text-base leading-snug",
            isMissing ? "font-semibold text-accent-ink" : "font-medium text-ink"
          )}
        >
          {value}
        </span>
        {hint ? (
          <span className="mt-1 block text-ink-3 text-sm leading-snug">
            {hint}
          </span>
        ) : null}
      </span>
      {isMissing ? (
        <span
          aria-hidden="true"
          className="size-2 shrink-0 rounded-full bg-accent"
        />
      ) : null}
      {onClick ? (
        <span className="shrink-0 text-ink-3">
          <Icon icon={ChevronRight} size={20} />
        </span>
      ) : null}
    </>
  );
  const classes = cx(
    "flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left",
    first !== true && "border-line border-t"
  );
  if (!onClick) {
    return <div className={classes}>{body}</div>;
  }
  return (
    <button
      className={cx(
        classes,
        "transition-colors hover:bg-canvas active:bg-wash"
      )}
      onClick={onClick}
      type="button"
    >
      {body}
    </button>
  );
}

/** A {@link Row} that opens editor `edit`, inside its own `Flash`. */
export function EditRow({
  edit,
  flashIds,
  open,
  flash,
  ...row
}: Omit<RowProps, "onClick"> &
  SectionProps & {
    readonly edit: EditorId;
    /** Other saved ids that also flash this row (A2's `days-row`). */
    readonly flashIds?: readonly string[];
  }) {
  const onClick = useCallback(() => open(edit), [open, edit]);
  return (
    <Flash flash={flash} id={[edit, ...(flashIds ?? [])]}>
      <Row {...row} onClick={onClick} />
    </Flash>
  );
}

/** A button that opens editor `edit` (the pill actions: "Registrar peso"). */
export function EditButton({
  edit,
  open,
  className,
  children,
}: {
  children: ReactNode;
  className: string;
  edit: EditorId;
  open: (id: EditorId) => void;
}) {
  const onClick = useCallback(() => open(edit), [open, edit]);
  return (
    <button className={className} onClick={onClick} type="button">
      {children}
    </button>
  );
}

/** A row that edits nothing but still sits in the card rhythm. */
export function SwitchRow({
  label,
  hint,
  control,
}: {
  control: ReactNode;
  hint?: ReactNode;
  label: string;
}) {
  return (
    <div className="flex min-h-16 items-center gap-3 border-line border-t px-4 py-3">
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-base leading-snug">
          {label}
        </span>
        {hint ? (
          <span className="block text-ink-3 text-sm leading-snug">{hint}</span>
        ) : null}
      </span>
      {control}
    </div>
  );
}

export function Why({
  asks,
  children,
}: {
  asks: boolean;
  children: ReactNode;
}) {
  return (
    <p className="flex gap-2 text-ink-2 text-sm leading-snug">
      <span className="mt-px shrink-0 text-ink-3">
        <Icon icon={Info} size={16} />
      </span>
      <span>
        {asks ? (
          <span className="font-semibold text-ink">{whyPrefix()} </span>
        ) : null}
        {children}
      </span>
    </p>
  );
}

/** A2 `Missing` (:218): the section-title badge. */
export function MissingChip({ count }: { count: number }) {
  return <Chip tone="accent">{missingBadge({ count })}</Chip>;
}

/**
 * `true` inside a page that holds this one section alone ("Corpo e
 * medidas", "Dores e lesões", `D217`): the page's header already names it,
 * so the section draws only its reason, never its title a second time.
 */
export const LoneSection = createContext(false);

/**
 * A2 :178-216. Phone: heading, reason, answers, stacked. Wide desktop: a
 * settings row — heading and reason on the left, the answers beside them.
 * `id` is what `?section=` scrolls to.
 */
export function Section({
  id,
  badge,
  children,
}: {
  badge?: ReactNode;
  children: ReactNode;
  id: SectionId;
}) {
  const meta = SECTION_META[id];
  const lone = useContext(LoneSection);
  if (lone) {
    return (
      <section
        aria-label={meta.title()}
        className="p-section"
        data-section={id}
        id={`p-${id}`}
      >
        <div className="flex items-start gap-3 px-1">
          <div className="min-w-0 flex-1">
            <Why asks={meta.asks}>{meta.why()}</Why>
          </div>
          {badge}
        </div>
        <div className="mt-3.5">{children}</div>
      </section>
    );
  }
  return (
    <section
      aria-labelledby={`p-${id}-title`}
      className="p-section xl:grid xl:grid-cols-[232px_minmax(0,1fr)] xl:gap-10 xl:border-line xl:border-t xl:pt-10"
      data-section={id}
      id={`p-${id}`}
    >
      <div>
        <div className="flex items-center gap-3 px-1 xl:flex-wrap xl:px-0">
          <span className="grid size-10 shrink-0 place-items-center rounded-md bg-surface text-ink-2 shadow">
            <Icon icon={meta.icon} size={20} />
          </span>
          <h2
            className="a-display min-w-0 flex-1 text-xl leading-tight xl:basis-full"
            id={`p-${id}-title`}
          >
            {meta.title()}
          </h2>
          {badge}
        </div>
        <div className="mt-2.5 px-1 xl:mt-3 xl:px-0">
          <Why asks={meta.asks}>{meta.why()}</Why>
        </div>
      </div>
      <div className="mt-3.5 xl:mt-0">{children}</div>
    </section>
  );
}

/**
 * Briefly tints a row that was just saved, so the edit is seen to stick
 * (A2 :755-762). The key changes with the flash, so the animation restarts
 * on a second save of the same row.
 */
export function Flash({
  id,
  flash,
  rounded,
  children,
}: {
  children: ReactNode;
  flash: string | null;
  id: string | readonly string[];
  rounded?: boolean;
}) {
  const ids: readonly string[] = typeof id === "string" ? [id] : id;
  const on = flash !== null && ids.includes(flash);
  const name = ids.join("|");
  return (
    <div
      className={cx(
        on && (rounded === true ? "rounded-2xl p-saved-ring" : "p-saved")
      )}
      key={on ? `${name}-on` : name}
    >
      {children}
    </div>
  );
}

/** Loading: the shape of a card of rows, never a spinner per row. */
export function SkeletonCard({ rows }: { rows: number }) {
  return (
    <Card>
      <div aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => (
          <div
            className={cx(
              "flex min-h-16 flex-col justify-center gap-2 px-4 py-3",
              i > 0 && "border-line border-t"
            )}
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-length placeholder
            key={i}
          >
            <span className="h-3 w-24 animate-pulse rounded-full bg-surface-2 motion-reduce:animate-none" />
            <span className="h-4 w-40 animate-pulse rounded-full bg-surface-2 motion-reduce:animate-none" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/** A read that failed with nothing cached: say so, offer to retry. */
export function LoadFailed({ retry }: { retry: () => void }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <p className="min-w-0 flex-1 text-base text-ink-2 leading-snug">
        {loadFailedText()}
      </p>
      <button
        className="a-press h-12 shrink-0 rounded-full bg-surface-2 px-4 font-semibold text-base text-ink"
        onClick={retry}
        type="button"
      >
        {retryText()}
      </button>
    </Card>
  );
}

/**
 * The common frame of a section body: skeleton while loading, the failure
 * card when it failed, the body otherwise.
 */
export function SlotBody({
  loading,
  failed,
  retry,
  rows,
  children,
}: {
  children: ReactNode;
  failed: boolean;
  loading: boolean;
  retry: () => void;
  rows: number;
}) {
  if (loading) {
    return <SkeletonCard rows={rows} />;
  }
  if (failed) {
    return <LoadFailed retry={retry} />;
  }
  return <>{children}</>;
}
